import { io } from 'socket.io-client';
import agoraService from './agoraService';
import axios from 'axios';

class CallService {
    constructor() {
        // Core state
        this.socket = null;
        this.isInitialized = false;
        this.currentPeerId = null;
        this.isReconnecting = false;
        this.currentRole = null;
        this.channelName = null;
        this.callStartTime = null;
        this.matchData = null;  // Add this to store complete match data
        this.myRole = null;  // Add this to store our role
        this.isProcessingMatch = false;

        // Enhanced state tracking
        this.connectionState = {
            status: 'disconnected',
            lastConnected: null,
            reconnectAttempts: 0,
            maxReconnectAttempts: 3
        };

        this.heartbeatState = {
            lastHeartbeat: null,
            lastAcknowledged: null,
            missedHeartbeats: 0
        };

        this.callMetrics = {
            duration: 0,
            quality: {
                audio: 'unknown',
                connection: 'unknown'
            },
            disconnections: 0,
            lastQualityCheck: null
        };

        // Intervals and timeouts
        this.heartbeatInterval = null;
        this.audioCheckInterval = null;
        this.reconnectTimeout = null;
        this.qualityCheckInterval = null;

        // Constants
        this.HEARTBEAT_INTERVAL = 20000; // 20 seconds
        this.RECONNECT_DELAY = 2000;
        this.QUALITY_CHECK_INTERVAL = 5000;
        this.MAX_RECONNECT_ATTEMPTS = 3;
        this.MAX_MISSED_HEARTBEATS = 3;
        this.SOCKET_CONNECTION_TIMEOUT = 60000; // 60 seconds

        // Callback storage
        this.callbacks = {
            onCallStatusChange: null,
            onMatchFound: null,
            onCallEnded: null,
            onCallQualityUpdate: null,
            onError: null
        };

        // Bind methods
        this.handleCallError = this.handleCallError.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.initialize = this.initialize.bind(this);
        this.startHeartbeat = this.startHeartbeat.bind(this);
        this.checkHeartbeatHealth = this.checkHeartbeatHealth.bind(this);
        // this.handleConnectionStateChange = this.handleConnectionStateChange.bind(this);
        // this.handleUserPublished = this.handleUserPublished.bind(this);
    }

async handleSocketConnection() {
    try {
            // Return true if socket is already connected
            if (this.socket?.connected) {
            window.socket = this.socket; // Add this line
            return true;
            }
    
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    this.socket.close();
                    reject(new Error('Socket connection timeout'));
                }, 60000); // 60 seconds
    
                this.socket.once('connect', () => {
                    clearTimeout(timeout);
                    console.log('Socket connected successfully');
                    this.connectionState.lastConnected = Date.now();
                    this.connectionState.status = 'connected';
                    window.socket = this.socket; // Add this line
                    resolve(true);
                });
    
                this.socket.once('connect_error', (error) => {
                    clearTimeout(timeout);
                    reject(error);
                });
    
                if (!this.socket.connected) {
                    this.socket.connect();
                }
            });
        } catch (error) {
            console.error('Socket connection error:', error);
            throw error;
        }
    }

async validateConnection() {
    try {
            // Check socket connection
            if (!this.socket?.connected) {
                throw new Error('Socket not connected');
            }
    
            // Verify audio
            const audioState = await agoraService.diagnoseMissingAudio();
            if (!audioState?.localTrack?.exists) {
                throw new Error('Audio validation failed');
            }
    
            // Verify channel state
            const response = await axios.get(
                '/api/call/validate-state',
                { channelName: this.channelName },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }
            );
    
            if (!response.data.valid) {
                throw new Error('Invalid call state');
            }
    
            return true;
        } catch (error) {
            console.error('Connection validation failed:', error);
            return false;
        }
    }

    async attemptReconnection() {
        try {
            // Enhanced state checks
            const skipReconnection = 
                this.isCleaningUp || 
                this.connectionState.status === 'disconnected' ||
                this.connectionState.status === 'rating';
    
            if (skipReconnection) {
                console.log('Skipping reconnection:', {
                    isCleaningUp: this.isCleaningUp,
                    connectionStatus: this.connectionState.status,
                    timestamp: new Date().toISOString()
                });
                return false;
            }
    
            // Already connected check
            if (this.socket?.connected && this.connectionState.status === 'connected') {
                console.log('Already connected, skipping reconnection');
                return true;
            }
    
            this.isReconnecting = true;
            console.log('Attempting reconnection...', {
                attempts: this.connectionState.reconnectAttempts,
                maxAttempts: this.MAX_RECONNECT_ATTEMPTS,
                timestamp: new Date().toISOString()
            });
    
            // Check max attempts
            if (this.connectionState.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
                throw new Error('Maximum reconnection attempts reached');
            }
    
            // Update state
            this.connectionState.status = 'reconnecting';
            this.connectionState.reconnectAttempts++;
            this.callbacks.onCallStatusChange?.('reconnecting');
    
            // Wait before attempting reconnection
            await new Promise(resolve => setTimeout(resolve, this.RECONNECT_DELAY));
    
            // Only try socket reconnection if needed
            if (!this.socket?.connected) {
                console.log('Reconnecting socket...');
                this.socket.connect();
                window.socket = this.socket;
            }
    
            // Only rejoin channel if in active call
            if (this.channelName && this.connectionState.status !== 'disconnected') {
                console.log('Rejoining channel:', this.channelName);
                await agoraService.join(this.channelName);
                const isValid = await this.validateConnection();
                if (!isValid) {
                    throw new Error('Connection validation failed');
                }
            }
    
            return true;
    
        } catch (error) {
            console.error('Reconnection attempt failed:', {
                error: error.message,
                connectionState: this.connectionState.status,
                attempts: this.connectionState.reconnectAttempts,
                timestamp: new Date().toISOString()
            });
            this.isReconnecting = false;
            return false;
        }
    }

checkHeartbeatHealth() {
    const now = Date.now();
        if (this.heartbeatState.lastAcknowledged) {
            const timeSinceLastAck = now - this.heartbeatState.lastAcknowledged;
            if (timeSinceLastAck > this.HEARTBEAT_INTERVAL * 2) {
                this.heartbeatState.missedHeartbeats++;
                if (this.heartbeatState.missedHeartbeats >= this.MAX_MISSED_HEARTBEATS) {
                    console.warn('Maximum missed heartbeats reached, initiating reconnection');
                    this.attemptReconnection();
                }
            }
        }
    }

startHeartbeat() {
    if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        
        this.heartbeatInterval = setInterval(() => {
            if (this.socket?.connected) {
                this.socket.emit('heartbeat');
                this.checkHeartbeatHealth();
            }
        }, this.HEARTBEAT_INTERVAL);
    
        // Send initial heartbeat
        if (this.socket?.connected) {
            this.socket.emit('heartbeat');
        }
    }

    async baseCleanup() {
        console.log('Starting base cleanup:', {
            hasMatchData: !!this.matchData,
            currentState: {
                channelName: this.channelName,
                currentPeerId: this.currentPeerId,
                role: this.currentRole
            },
            timestamp: new Date().toISOString()
        });
    
        try {
            // Clear all intervals and timeouts
            [
                this.heartbeatInterval,
                this.audioCheckInterval,
                this.qualityCheckInterval
            ].forEach(interval => {
                if (interval) clearInterval(interval);
            });
    
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
            }

            // Add here - after clearing timeouts but before other state resets
            this.isProcessingMatch = false;  // Reset processing state early
    
            // Reset interval references
            this.heartbeatInterval = null;
            this.audioCheckInterval = null;
            this.qualityCheckInterval = null;
            this.reconnectTimeout = null;
    
            // Reset heartbeat state
            this.heartbeatState = {
                lastHeartbeat: null,
                lastAcknowledged: null,
                missedHeartbeats: 0
            };
    
            // Clean up Agora
            await agoraService.cleanup();
    
            // Store temporary match data for logging
            const previousMatchData = this.matchData;
    
            // Reset all user-related state
            this.channelName = null;
            this.currentPeerId = null;
            this.currentRole = null;
            this.callStartTime = null;
            this.isReconnecting = false;
            this.matchData = null;  // Clear match data
    
            // Reset metrics
            this.callMetrics = {
                duration: 0,
                quality: {
                    audio: 'unknown',
                    connection: 'unknown'
                },
                disconnections: 0,
                lastQualityCheck: null
            };
    
            // Reset connection state but preserve socket status
            this.connectionState = {
                status: this.socket?.connected ? 'connected' : 'disconnected',
                lastConnected: this.socket?.connected ? Date.now() : null,
                reconnectAttempts: 0,
                maxReconnectAttempts: 3
            };
    
            console.log('Base cleanup completed:', {
                previousData: previousMatchData,  // Log previous data
                currentState: {
                    hasMatchData: !!this.matchData,
                    channelName: this.channelName,
                    currentPeerId: this.currentPeerId,
                    socketStatus: this.connectionState.status
                },
                timestamp: new Date().toISOString()
            });
    
            return true;
    
        } catch (error) {
            console.error('Error during base cleanup:', {
                error: error.message,
                stack: error.stack,
                matchData: this.matchData,  // Log match data in error
                timestamp: new Date().toISOString()
            });

            // Add here - before resetting other critical states
            this.isProcessingMatch = false;  // Force reset even on error
    
            // Reset critical states if cleanup fails
            this.channelName = null;
            this.currentPeerId = null;
            this.currentRole = null;
            this.matchData = null;  // Ensure match data is cleared
            this.connectionState.status = 'error';
    
            return false;
        }
    }

handleGenericError(error) {
    console.error('Generic error:', error);
        
        this.callbacks.onError?.({
            type: 'generic',
            message: error.message || 'An unexpected error occurred',
            recoverable: false
        });
}

handlePermissionError(error) {
    console.error('Permission error:', error);
    
    this.callbacks.onError?.({
        type: 'permission',
        message: 'Microphone access is required for calls',
        recoverable: false
    });
}

handleNetworkError(error) {
    console.error('Network error:', error);
    
    try {
        // Attempt reconnection if possible
        if (this.connectionState.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
            this.attemptReconnection();
            
            this.callbacks.onError?.({
                type: 'network',
                message: 'Network issue detected. Attempting reconnection...',
                recoverable: true
            });
        } else {
            console.error('Max reconnection attempts reached');
            this.callbacks.onError?.({
                type: 'network',
                message: 'Unable to reconnect after multiple attempts',
                recoverable: false
            });
        }
    } catch (reconnectError) {
        console.error('Network recovery failed:', reconnectError);
        this.callbacks.onError?.({
            type: 'network',
            message: 'Network recovery failed',
            recoverable: false
        });
    }
}

async handleAudioError(error) {
    console.error('Audio error:', {
        error: error.message,
        isCleaningUp: this.isCleaningUp,
        connectionState: this.connectionState.status,
        timestamp: new Date().toISOString()
    });

    // Skip recovery if cleanup in progress
    if (this.isCleaningUp) {
        console.log('Skipping audio recovery - cleanup in progress');
        return;
    }

    try {
        // Use Agora's direct recovery
        const recovered = await agoraService.handleAudioRecovery();

        if (!recovered) {
            this.callbacks.onError?.({
                type: 'audio',
                message: 'Audio recovery failed',
                recoverable: false,
                context: 'normal_operation'
            });
        } else {
            this.callbacks.onError?.({
                type: 'audio',
                message: 'Audio recovered successfully',
                recoverable: true,
                context: 'normal_operation'
            });
        }
    } catch (recoveryError) {
        console.error('Audio recovery failed:', {
            error: recoveryError.message,
            isCleaningUp: this.isCleaningUp,
            timestamp: new Date().toISOString()
        });

        // Only notify if not cleaning up
        if (!this.isCleaningUp) {
            this.callbacks.onError?.({
                type: 'audio',
                message: 'Audio recovery failed',
                recoverable: false,
                context: 'recovery_attempt'
            });
        }
    }
}

    // Error handling and recovery
handleCallError(error) {
    console.error('Call error:', error);
    
    try {
        // Update connection state
        this.connectionState = {
            ...this.connectionState,
            status: 'error',
            lastError: {
                message: error.message,
                timestamp: Date.now()
            }
        };

        // Handle specific error types
        if (error.type === 'audio') {
            this.handleAudioError(error);
        } else if (error.type === 'network') {
            this.handleNetworkError(error);
        } else if (error.type === 'permission') {
            this.handlePermissionError(error);
        } else {
            this.handleGenericError(error);
        }

    } catch (handlingError) {
        console.error('Error in error handler:', handlingError);
        this.baseCleanup();
        this.callbacks.onError?.('Critical error occurred');
    }
}

async handleAudioRecovery() {
    try {
        // Check for rating dialog
        if (this.myRole === 'talker' && this.callbacks.onCallStatusChange === 'rating') {
            console.log('Skipping audio recovery - showing rating dialog');
            return false;
        }

        // Check channel and cleanup state
        if (!this.channelName || this.isCleaningUp) {
            console.log('Skipping audio recovery - cleanup or no channel');
            return false;
        }

        console.log('Attempting audio recovery...');

        // Try Agora service recovery first
        try {
            const agoraRecovered = await agoraService.handleAudioRecovery();
            if (agoraRecovered) {
                console.log('Audio recovered through Agora service');
                return true;
            }
        } catch (agoraError) {
            console.log('Agora direct recovery failed, attempting full reconnection');
        }

        // If direct recovery fails, try full reconnection
        await agoraService.leave();
        await agoraService.cleanup();
        await agoraService.initialize();
        await agoraService.join(this.channelName);

        // Verify final state
        const diagnosis = await agoraService.diagnoseMissingAudio();
        if (!diagnosis?.localTrack?.exists) {
            throw new Error('Audio recovery failed after reconnection');
        }

        console.log('Audio recovery completed successfully');
        return true;

    } catch (error) {
        console.error('Audio recovery failed:', error);
        this.handleCallError(new Error('Failed to recover audio connection'));
        return false;
    }
}

startCallMonitoring() {
    if (this.audioCheckInterval) {
            clearInterval(this.audioCheckInterval);
        }
    
        this.audioCheckInterval = setInterval(async () => {
            try {
                if (this.channelName) {
                    const diagnosis = await agoraService.diagnoseMissingAudio();
                    
                    if (!diagnosis?.localTrack?.exists || !diagnosis?.localTrack?.enabled) {
                        console.warn('Audio issues detected, attempting recovery...');
                        await this.handleAudioRecovery();
                    }
    
                    // Update call metrics
                    if (this.callStartTime) {
                        this.callMetrics.duration = Math.floor((Date.now() - this.callStartTime) / 1000);
                    }
                }
            } catch (error) {
                console.error('Call monitoring error:', error);
            }
        }, 2000);
    }

    // Match and call handling
    async handleMatchFound(matchData) {
        console.log('Match event received:', {
            matchData,
            currentState: {
                channelName: this.channelName,
                currentPeerId: this.currentPeerId,
                isProcessing: this.isProcessingMatch,
                isInitialized: this.isInitialized,
                timestamp: new Date().toISOString()
            }
        });
    
        // 1. Early validation of match data
        if (!matchData?.channelName || !matchData?.userId || !matchData?.role) {
            console.error('Invalid match data:', matchData);
            return;
        }
    
        // 2. Check if we're already connected to this channel
        if (this.channelName === matchData.channelName) {
            console.log('Already connected to this channel:', this.channelName);
            return;
        }
    
        // 3. Check processing state
        if (this.isProcessingMatch) {
            console.log('Match processing in progress, skipping');
            return;
        }
    
        // 4. Set processing lock
        this.isProcessingMatch = true;
        
        try {
            // 5. Store channel name first
            const newChannelName = matchData.channelName;
            
            // 6. Clean existing connection if any
            if (this.channelName) {
                console.log('Cleaning existing connection:', this.channelName);
                await this.baseCleanup();
            }
    
            // 7. Store complete match data
            this.matchData = {
                ...matchData,
                startTime: Date.now(),
                userId: matchData.userId,
                username: matchData.username || '',
                role: matchData.role,
                interests: matchData.interests || '',
                mood: matchData.mood || '',
                languages: matchData.languages || ['English'],
                stars: matchData.stars || 0,
                ratingCount: matchData.ratingCount || 0,
                channelName: newChannelName
            };
    
            console.log('Stored match data:', {
                storedData: this.matchData,
                myRole: this.myRole,
                timestamp: new Date().toISOString()
            });
    
            // 8. Verify audio setup
            console.log('Verifying audio setup');
            const audioValidation = await agoraService.verifyAudioTrack();
            if (!audioValidation?.isValid) {
                throw new Error('Audio setup not ready: ' + JSON.stringify(audioValidation.details));
            }
    
            // 9. Set core states
            this.channelName = newChannelName;
            this.currentPeerId = matchData.userId;
            this.callStartTime = Date.now();
    
            console.log('Updated match state:', {
                userId: this.currentPeerId,
                channelName: this.channelName,
                myRole: this.myRole,
                matchedUserRole: matchData.role,
                timestamp: new Date().toISOString()
            });
    
            // 10. Join and verify channel
            await agoraService.join(this.channelName);
            await new Promise(resolve => setTimeout(resolve, 1000));
    
            let verificationSuccess = false;
            for (let i = 0; i < 3; i++) {
                const diagnosis = await agoraService.diagnoseMissingAudio();
                if (diagnosis?.localTrack?.exists && diagnosis?.localTrack?.enabled) {
                    verificationSuccess = true;
                    console.log('Channel verification succeeded');
                    break;
                }
                if (i < 2) {
                    console.log(`Channel verification attempt ${i + 1} failed, retrying...`);
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
    
            if (!verificationSuccess) {
                throw new Error('Failed to verify channel connection');
            }
    
            // 11. Start monitoring and emit ready
            this.startCallMonitoring();
            
            if (this.socket?.connected) {
                console.log('Emitting ready state');
                this.socket.emit('userReady', {
                    channelName: this.channelName,
                    peerId: this.currentPeerId,
                    myRole: this.myRole,
                    matchData: this.matchData,
                    audioReady: true
                });
            }
    
            console.log('Match handling completed:', {
                channelName: this.channelName,
                peerId: this.currentPeerId,
                myRole: this.myRole,
                timestamp: new Date().toISOString()
            });
    
            return true;
    
        } catch (error) {
            // 12. Comprehensive cleanup on error
            console.error('Match handling error:', {
                error: error.message,
                stack: error.stack,
                channelName: this.channelName,
                timestamp: new Date().toISOString()
            });
    
            await this.baseCleanup();
            this.channelName = null;
            this.currentPeerId = null;
            this.matchData = null;
            throw error;
            
        } finally {
            // 13. Reset processing flag only if no channel established
            if (!this.channelName) {
                this.isProcessingMatch = false;
                console.log('Reset processing flag - no channel established');
            }
        }
    }
    
async toggleMute() {
    try {
        return await agoraService.toggleMute();
    } catch (error) {
        console.error('Toggle mute error:', error);
        throw error;
    }
}

async handleCallEnded(reason = 'unknown') {
    try {
        console.log('Call ended:', {
            reason,
            myRole: this.myRole,
            timestamp: new Date().toISOString()
        });

        // Clear monitoring intervals
        if (this.audioCheckInterval) {
            clearInterval(this.audioCheckInterval);
            this.audioCheckInterval = null;
        }

        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }

        // Add here - after clearing intervals but before handling roles
        this.isProcessingMatch = false;  // Reset processing state

        // Collect final metrics
        const finalMetrics = {
            ...this.callMetrics,
            endTime: Date.now(),
            endReason: reason,
            finalDuration: this.callStartTime ? 
                Math.floor((Date.now() - this.callStartTime) / 1000) : 0
        };

        // Role-specific handling
        if (this.myRole === 'talker') {
            // Preserve match data for rating
            this.callbacks.onCallEnded?.(reason, {
                ...finalMetrics,
                preserveForRating: true,
                match: this.matchData
            });
        } else {
            // Clean listener metrics
            this.callbacks.onCallEnded?.(reason, finalMetrics);
        }

    } catch (error) {
        console.error('Error ending call:', error);
        // Add here - ensure reset even on error
        this.isProcessingMatch = false;  // Force reset on error
        this.callbacks.onCallEnded?.('Error ending call');
    }
}

async endCall() {
    try {
        console.log('Ending call:', {
            channelName: this.channelName,
            myRole: this.myRole,
            timestamp: new Date().toISOString()
        });

        if (!this.channelName) {
            return false;
        }

        // Clear intervals
        [
            this.audioCheckInterval,
            this.qualityCheckInterval,
            this.heartbeatInterval
        ].forEach(interval => {
            if (interval) clearInterval(interval);
        });

        // Prepare end call data
        const endCallData = {
            channelName: this.channelName,
            timestamp: Date.now(),
            myRole: this.myRole,
            match: this.matchData
        };

        // Emit end call event
        if (this.socket?.connected) {
            await new Promise((resolve) => {
                this.socket.emit('endCall', endCallData, (response) => {
                    resolve(response?.success);
                });
                setTimeout(resolve, 1000);
            });
        }

        // Role-specific cleanup
        if (this.myRole === 'listener') {
            await this.baseCleanup();
            this.callbacks.onCallEnded?.('user_ended');
        } else {
            // Talker: Only clean audio, preserve match data
            await agoraService.cleanup();
            this.callbacks.onCallEnded?.('user_ended', {
                preserveForRating: true,
                match: this.matchData
            });
        }

        return true;
    } catch (error) {
        console.error('End call error:', error);
        return false;
    }
}

async handlePeerDisconnection(data) {
    try {
        this.callMetrics.disconnections += 1;
        
        if (this.callMetrics.disconnections >= this.MAX_RECONNECT_ATTEMPTS) {
            await this.handleCallEnded('Peer disconnected permanently');
            return;
        }

        // Wait for peer reconnection
        this.callbacks.onCallStatusChange?.('reconnecting');
        
        // Start reconnection timeout
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        this.reconnectTimeout = setTimeout(async () => {
            if (this.channelName) {
                await this.handleCallEnded('Peer reconnection timeout');
            }
        }, 15000);

    } catch (error) {
        console.error('Error handling peer disconnection:', error);
        await this.handleCallEnded('Error handling peer disconnection');
    }
}

async handleQualityUpdate(data) {
    try {
        this.callMetrics.quality = {
            ...this.callMetrics.quality,
            ...data,
            lastUpdate: Date.now()
        };

        if (data.audio === 'poor' || data.connection === 'poor') {
            console.warn('Poor call quality detected:', data);
            this.callbacks.onCallQualityUpdate?.(this.callMetrics.quality);
        }
    } catch (error) {
        console.error('Error handling quality update:', error);
    }
}

async checkServiceHealth() {
    try {
            const audioState = await agoraService.diagnoseMissingAudio();
            const connectionState = this.socket.connected && audioState?.localTrack?.exists;
            
            if (!connectionState) {
                throw new Error('Service health check failed');
            }
    
            return {
                healthy: true,
                audioState,
                connectionState: this.connectionState,
                callMetrics: this.callMetrics
            };
        } catch (error) {
            console.error('Health check failed:', error);
            this.handleServiceError(error);
            return { healthy: false, error: error.message };
        }
    }

    async checkCallQuality() {
        // Skip quality checks if cleaning up or disconnected
        if (this.isCleaningUp || this.connectionState.status !== 'connected') {
            console.log('Skipping quality check - cleanup or not connected');
            return null;
        }
    
        try {
            // Only get stats if we have active connection
            if (!this.socket?.connected || !this.channelName) {
                return null;
            }
    
            const audioQuality = await agoraService.getCallStats();
            const networkQuality = await this.socket.volatile.emit('getNetworkStats');
    
            // Update metrics with timestamp
            const qualityMetrics = {
                audio: audioQuality,
                connection: networkQuality,
                timestamp: Date.now()
            };
    
            // Store in call metrics
            this.callMetrics.quality = qualityMetrics;
    
            // Only notify if still connected and not cleaning up
            if (this.connectionState.status === 'connected' && !this.isCleaningUp) {
                this.callbacks.onCallQualityUpdate?.(qualityMetrics);
            }
    
            return qualityMetrics;
    
        } catch (error) {
            // Only log errors if not in cleanup
            if (!this.isCleaningUp) {
                console.error('Quality check error:', {
                    error: error.message,
                    status: this.connectionState.status,
                    cleaning: this.isCleaningUp,
                    timestamp: new Date().toISOString()
                });
            }
            return null;
        }
    }
 
    // Connection monitoring
 startServiceMonitoring() {
    // Heartbeat monitoring
    if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
        try {
            if (this.isInitialized && this.socket?.connected) {
                this.checkHeartbeatHealth();
                this.checkServiceHealth();
            }
        } catch (error) {
            console.error('Service health check failed:', error);
        }
    }, this.HEARTBEAT_INTERVAL);

    // Call quality monitoring
    if (this.qualityCheckInterval) {
        clearInterval(this.qualityCheckInterval);
    }

    this.qualityCheckInterval = setInterval(async () => {
        try {
            if (this.channelName) {
                await this.checkCallQuality();
            }
        } catch (error) {
            console.error('Quality check failed:', error);
        }
    }, this.QUALITY_CHECK_INTERVAL);
}

setupSocketEventHandlers() {
    if (!this.socket) return;
        
        this.socket.removeAllListeners();
        
        this.socket.on('connect', () => {
            console.log('Socket connected:', this.socket.id);
            this.connectionState.reconnectAttempts = 0;
            this.connectionState.lastConnected = Date.now();
            this.connectionState.status = 'connected';
            this.startHeartbeat();
        });

        // Add handlers for temporary disconnect events
    this.socket.on('peerTemporaryDisconnect', (data) => {
        console.log('Peer temporary disconnect:', {
            channelName: this.channelName,
            timestamp: new Date().toISOString()
        });

        // Notify UI of peer disconnection
        this.callbacks.onCallStatusChange?.('peer_disconnected');
    });

    this.socket.on('peerPermanentDisconnect', async (data) => {
        console.log('Peer permanent disconnect:', {
            channelName: this.channelName,
            timestamp: new Date().toISOString()
        });

        // Clean up call and notify UI
        await this.handleCallEnded('peer_disconnect');
    });

    this.socket.on('peerReconnected', async (data) => {
        console.log('Peer reconnected:', {
            channelName: data.channelName,
            timestamp: new Date().toISOString()
        });

        try {
            // Rejoin Agora channel with existing token
            await agoraService.join(data.channelName, data.agoraToken);
            
            // Verify audio setup after rejoining
            const audioState = await agoraService.verifyAudioTrack();
            if (!audioState.isValid) {
                throw new Error('Audio verification failed after reconnection');
            }

            // Update UI back to connected state
            this.callbacks.onCallStatusChange?.('connected');
            
            // Start monitoring again
            this.startCallMonitoring();

        } catch (error) {
            console.error('Error handling peer reconnection:', error);
            await this.handleCallEnded('reconnection_failed');
        }
    });
    
        // Add heartbeat handlers
        this.socket.on('heartbeat', () => {
            this.heartbeatState.lastHeartbeat = Date.now();
            this.socket.emit('heartbeat-ack');
        });

        this.socket.on('heartbeat-response', () => {             
            this.heartbeatState.lastAcknowledged = Date.now();             
            this.heartbeatState.missedHeartbeats = 0;         
        });
    
        this.socket.on('disconnect', async (reason) => {
            console.log('Socket disconnected:', reason);
            if (this.heartbeatInterval) {
                clearInterval(this.heartbeatInterval);
                this.heartbeatInterval = null;
            }
            if (!['io server disconnect', 'io client disconnect'].includes(reason)) {
                await this.attemptReconnection();
            }
        });
    
        this.socket.on('matchFound', async (data) => {
            console.log('matchFound event received:', {
                data,
                timestamp: new Date().toISOString(),
                currentState: {
                    channelName: this.channelName,
                    isInitialized: this.isInitialized,
                    callStatus: this.connectionState.status,
                    isProcessingMatch: this.isProcessingMatch
                }
            });
        
            // Guard against multiple matches or existing call
            if (this.isProcessingMatch || this.currentChannel) {
                console.log('Skipping match - already processing or in channel:', {
                    isProcessing: this.isProcessingMatch,
                    currentChannel: this.currentChannel,
                    timestamp: new Date().toISOString()
                });
                return;
            }
        
            try {
                // Validate match data
                if (!data?.channelName || !data?.userId) {
                    console.error('Invalid match data:', data);
                    throw new Error('Invalid match data received');
                }
        
                console.log('Match data validation passed, processing match:', {
                    channelName: data.channelName,
                    userId: data.userId,
                    myRole: this.myRole,
                    timestamp: new Date().toISOString()
                });
        
                // Handle match
                await this.handleMatchFound(data);
        
                console.log('Match handling completed successfully:', {
                    channelName: data.channelName,
                    myRole: this.myRole,
                    timestamp: new Date().toISOString()
                });
        
            } catch (error) {
                console.error('Match handling error:', {
                    error: error.message,
                    stack: error.stack,
                    data,
                    currentState: {
                        channelName: this.channelName,
                        connectionState: this.connectionState,
                        myRole: this.myRole
                    },
                    timestamp: new Date().toISOString()
                });
        
                // Ensure cleanup on error
                try {
                    await this.baseCleanup();
                } catch (cleanupError) {
                    console.error('Cleanup after match error failed:', cleanupError);
                }
        
                this.handleCallError(error);
            }
        });
    
        this.socket.on('callReady', ({ channelName }) => {
            console.log('callReady event received:', {
                channelName,
                currentChannelName: this.channelName,
                hasCallbacks: !!this.callbacks.onCallStatusChange,
                hasMatchData: !!this.matchData,  // Log match data presence
                timestamp: new Date().toISOString()
            });
        
            try {
                // Enhanced validation
                if (!this.channelName || !this.matchData) {
                    console.warn('Missing required data:', {
                        hasChannel: !!this.channelName,
                        hasMatchData: !!this.matchData,
                        timestamp: new Date().toISOString()
                    });
                    return;
                }
        
                // Verify channel match
                if (this.channelName !== channelName) {
                    console.warn('Channel mismatch:', {
                        receivedChannel: channelName,
                        currentChannel: this.channelName,
                        timestamp: new Date().toISOString()
                    });
                    return;
                }
        
                // Update call status with complete match data
                console.log('Updating call status to connected with match data:', {
                    matchData: this.matchData,
                    timestamp: new Date().toISOString()
                });
        
                if (this.callbacks.onCallStatusChange) {
                    // Use complete stored match data
                    this.callbacks.onCallStatusChange('connected', {
                        ...this.matchData,  // Spread all stored match data
                        channelName: this.channelName,
                        connectionTime: Date.now(),
                        myRole: this.myRole,
                        // Ensure critical fields exist
                        userId: this.matchData.userId,
                        matchedRole: this.matchData.role,
                        username: this.matchData.username,
                        interests: this.matchData.interests || '',
                        mood: this.matchData.mood || '',
                        languages: this.matchData.languages || ['English'],
                        stars: this.matchData.stars || 0,
                        ratingCount: this.matchData.ratingCount || 0
                    });
                } else {
                    console.warn('No status change callback available');
                }
        
                // Start monitoring
                this.startServiceMonitoring();
        
                // Enhanced completion logging
                console.log('Call ready processing completed:', {
                    channelName,
                    callMetrics: this.callMetrics,
                    matchData: this.matchData,  // Log complete match data
                    currentState: {
                        peerId: this.currentPeerId,
                        myRole: this.myRole,
                        channelName: this.channelName
                    },
                    timestamp: new Date().toISOString()
                });
        
            } catch (error) {
                console.error('Error processing callReady event:', {
                    error: error.message,
                    stack: error.stack,
                    channelName,
                    currentState: {
                        channel: this.channelName,
                        callMetrics: this.callMetrics,
                        matchData: this.matchData,  // Include match data in error
                        peerId: this.currentPeerId
                    },
                    timestamp: new Date().toISOString()
                });
        
                // Try to recover on error
                try {
                    this.startServiceMonitoring();  // Still try to start monitoring
                } catch (monitorError) {
                    console.error('Failed to start monitoring after error:', monitorError);
                }
            }
        });
    
        this.socket.on('callEnded', async (data) => {
            console.log('Call ended socket event:', {
                myRole: this.myRole,
                timestamp: new Date().toISOString()
            });
        
            try {
                // Simple role-based flow
                if (this.myRole === 'listener') {
                    // Listener: Full cleanup
                    await this.baseCleanup();
                    this.callbacks.onCallStatusChange?.('disconnected');
                    this.callbacks.onCallEnded?.('call_ended');
                } else {
                    // Talker: Clean audio but preserve data for rating
                    await agoraService.cleanup();
                    this.callbacks.onCallStatusChange?.('rating');
                    this.callbacks.onCallEnded?.('call_ended', {
                        match: this.matchData,
                        preserveForRating: true
                    });
                }
            } catch (error) {
                console.error('Error handling call end:', error);
                await this.baseCleanup();  // Emergency cleanup
            }
        });
    
        this.socket.on('peerDisconnected', async (data) => {
            console.log('Peer disconnected:', data);
            await this.handlePeerDisconnection(data);
        });
    
        // Health check events
        this.socket.on('healthCheck', async () => {
            const audioState = await agoraService.diagnoseMissingAudio();
            this.socket.emit('healthResponse', {
                audioState,
                callMetrics: this.callMetrics,
                timestamp: Date.now()
            });
        });
    
        // Error handling
        this.socket.on('error', error => {
            console.error('Socket error:', error);
            this.handleCallError(error);
        });
    
        // Quality updates
        this.socket.on('qualityUpdate', (data) => {
            this.handleQualityUpdate(data);
        });
    }

    async cleanup(force = false) {
        if (this.isCleaningUp) {
            console.log('Cleanup already in progress, skipping');
            return true;
        }
    
        this.isCleaningUp = true;
        console.log('Starting full cleanup:', {
            force,
            currentSocket: this.socket?.connected ? 'connected' : 'disconnected',
            timestamp: new Date().toISOString()
        });
    
        try {
            // Basic call state cleanup first
            await this.baseCleanup();
    
            // Handle force cleanup - complete disconnection
            if (force) {
                console.log('Force cleanup - removing socket listeners');
                this.socket?.removeAllListeners();
                this.socket?.disconnect();
            } else {
                // Non-forced cleanup: Just refresh socket handlers
                if (this.socket?.connected) {
                    this.socket.removeAllListeners();
                    this.setupSocketEventHandlers();
                }
            }
    
            return true;
        } catch (error) {
            console.error('Error during cleanup:', error);
            return false;
        } finally {
            this.isCleaningUp = false;
        }
    }

    async joinAsRole(role) {
        try {
            // Enhanced validation
            if (!this.channelName || !this.matchData) {
                console.error('Join validation failed:', {
                    hasChannel: !!this.channelName,
                    hasMatchData: !!this.matchData,
                    role
                });
                throw new Error('No channel or match data available');
            }
     
            console.log(`Joining channel for role ${role}`, {
                channelName: this.channelName,
                myRole: this.myRole,
                matchData: this.matchData,  // Log complete match data
                timestamp: new Date().toISOString()
            });
     
            // No role-specific config needed since both are hosts
            await agoraService.join(this.channelName);
            
            // Start call monitoring right after join
            this.startCallMonitoring();
     
            // Emit ready state with original match data
            this.socket.emit('userReady', {
                channelName: this.channelName,
                myRole: this.myRole,                // Current user's role
                peerId: this.currentPeerId,
                matchData: this.matchData   // Keep original match data intact
            });
     
            console.log('Successfully joined channel:', {
                channelName: this.channelName,
                myRole: this.myRole,
                matchData: this.matchData,
                timestamp: new Date().toISOString()
            });
     
            return true;
     
        } catch (error) {
            console.error('Error joining channel:', {
                error: error.message,
                stack: error.stack,
                role,
                channelName: this.channelName,
                timestamp: new Date().toISOString()
            });
     
            // Clear match data on error
            this.matchData = null;
            await this.cleanup();
            throw error;
        }
     }

async initialize(onCallStatusChange, onMatchFound, onCallEnded, onCallQualityUpdate, onError) {
    if (this.isInitialized) {
        await this.cleanup();
    }

    console.log('Initializing call service...');
    
    try {
        // Store callbacks
        this.callbacks = {
            onCallStatusChange: onCallStatusChange || this.callbacks?.onCallStatusChange,
            onMatchFound: onMatchFound || this.callbacks?.onMatchFound,
            onCallEnded: onCallEnded || this.callbacks?.onCallEnded,
            onCallQualityUpdate: onCallQualityUpdate || this.callbacks?.onCallQualityUpdate,
            onError: onError || this.callbacks?.onError
        };

        // Validate required callbacks
        if (!this.callbacks.onCallStatusChange || !this.callbacks.onMatchFound || !this.callbacks.onCallEnded) {
            throw new Error('Missing required callback handlers');
        }

        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Add role retrieval right after token
        this.myRole = localStorage.getItem('userRole');
        if (!this.myRole) {
            console.warn('No user role found in storage');
        }

        // Initialize socket with enhanced configuration
        console.log('Setting up socket connection...');
        if (this.socket?.connected) {
            this.socket.disconnect();
        }

        // Enhanced socket configuration
        this.socket = io('https://api.talkconnect.live', {
            auth: { token },
            transports: ['websocket', 'polling'],
            timeout: 60000, // Increased timeout
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
            forceNew: true,
            path: '/socket.io/',
            closeOnBeforeunload: false,
            query: {
                deviceType: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
                browser: navigator.userAgent
            }
        });

        // Add this line
        window.socket = this.socket;

        // Enhanced error handling for socket
        this.socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            if (error.message === 'xhr poll error') {
                this.socket.io.opts.transports = ['websocket'];
            }
            // Emit error to callback
            this.callbacks.onError?.(error);
        });

        // Enhanced reconnection handling
        this.socket.io.on("reconnect_attempt", (attemptNumber) => {
            console.log(`Socket attempting to reconnect... Attempt ${attemptNumber}`);
            // Try both transport methods
            this.socket.io.opts.transports = ['websocket', 'polling'];
        });

        this.socket.io.on("reconnect_failed", () => {
            console.log('Socket reconnection failed, attempting manual reconnection...');
            setTimeout(() => {
                this.socket.connect();
            }, 2000);
        });

        // Setup base event handlers
        this.setupSocketEventHandlers();

        // Wait for connection with timeout
        try {
            await Promise.race([
                this.handleSocketConnection(),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Socket connection timeout')), 30000)
                )
            ]);
            console.log('Socket connected successfully');
            this.startHeartbeat(); // Start heartbeat after successful connection
        } catch (socketError) {
            console.error('Socket connection failed:', socketError);
            throw socketError;
        }

        // Initialize Agora only after socket connection
        try {
            await agoraService.initialize();
            console.log('Agora service initialized');
        } catch (agoraError) {
            console.error('Agora initialization failed:', agoraError);
            throw agoraError;
        }

        // Setup monitoring
        this.startServiceMonitoring();
        
        this.isInitialized = true;
        this.connectionState.status = 'connected';
        return true;

    } catch (error) {
        console.error('Error initializing call service:', error);
        await this.cleanup();
        this.isInitialized = false;
        this.connectionState.status = 'error';
        
        // Notify error through callback
        this.callbacks.onError?.(error);
        
        throw error;
    }
}

// Socket connection handling
waitForSocketConnection() {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error('Socket connection timeout'));
        }, 10000);

        const handleConnect = () => {
            clearTimeout(timeout);
            this.connectionState.lastConnected = Date.now();
            this.connectionState.status = 'connected';
            resolve();
        };

        const handleError = (error) => {
            clearTimeout(timeout);
            reject(error);
        };

        this.socket.once('connect', handleConnect);
        this.socket.once('connect_error', handleError);
    });
}

async handleReconnection() {
    try {
        const success = await this.attemptReconnection();
        
        if (success) {
            // Wait for socket connection after successful attempt
            await this.waitForSocketConnection();
            window.socket = this.socket;

            // Reset states after successful connection
            this.isReconnecting = false;
            this.connectionState.status = 'connected';
            this.callbacks.onCallStatusChange?.('connected');

            // Start heartbeat and monitoring
            this.startHeartbeat();
            if (this.channelName) {
                this.startCallMonitoring();
            }

            console.log('Reconnection successful');
            return true;
        } else {
            console.log('Reconnection failed, ending call');
            await this.handleCallEnded('Reconnection failed');
            return false;
        }
    } catch (error) {
        console.error('Error in handleReconnection:', error);
        this.isReconnecting = false;
        await this.handleCallEnded('Reconnection handling failed');
        return false;
    }
}

// Utility methods
isInCall() {
    return !!(this.channelName && this.currentPeerId && this.callStartTime);
}

getCallDuration() {
    if (!this.callStartTime) return 0;
    return Math.floor((Date.now() - this.callStartTime) / 1000);
}

getConnectionState() {
    return {
        ...this.connectionState,
        callActive: this.isInCall(),
        callDuration: this.getCallDuration(),
        metrics: this.callMetrics
    };
}
}

// Create and export singleton instance
const callServiceInstance = new CallService();
export default callServiceInstance;